// src/pages/dashboard/SettingsPage.tsx
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { updateProfile, changePassword, getMe } from '@/lib/services/auth';
import { requestEmailChangeCode, confirmEmailChangeCode } from '@/lib/services/recovery';
import type { RecoveryChannel } from '@/lib/services/recovery';
import { codeLength } from '@/lib/utils/codeLength';
import { CodeEntry } from '@/components/auth/CodeEntry';
import { VerificationStep } from '@/components/auth/VerificationStep';
import { ApiError } from '@/lib/api/client';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '@/lib/services/notifications';
import type { NotificationPreferencesUpdate } from '@/lib/services/notifications';
import { brand } from '@/lib/tokens';
import { PasswordConfirmModal } from '@/components/ui/PasswordConfirmModal';
import { PasswordField } from '@/components/ui/PasswordField';
import { useToast } from '@/context/ToastContext';
import { useTheme } from '@/context/ThemeContext';
import { usePushSubscription } from '@/lib/hooks/usePushSubscription';

type Section = 'profile' | 'session' | 'appearance' | 'notifications' | 'about';

const sections: Array<{ id: Section; label: string; meta: string }> = [
  { id: 'profile', label: 'Profile', meta: 'Operator identity' },
  { id: 'session', label: 'Session', meta: 'Access tunnel' },
  { id: 'appearance', label: 'Appearance', meta: 'Theme' },
  { id: 'notifications', label: 'Notifications', meta: 'Control room signals' },
  { id: 'about', label: 'About', meta: 'Build and workspace' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('profile');

  function signOut() {
    navigate('/login');
  }

  return (
    <main
      className="noir-workspace flex-1"
      style={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        className="mx-auto w-full settings-shell"
        style={{
          maxWidth: 1180,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* HEADER */}
        <header
          className="mb-10 grid grid-cols-[1fr_320px] gap-10 items-end settings-header"
          style={{
            flexShrink: 0,
          }}
        >
          <div>
            <span className="mono-label">Settings Console</span>

            <h1
              className="cinema-title mt-3 settings-heading"
              style={{
                fontSize: 46,
                lineHeight: 1.02,
              }}
            >
              Operator preferences
            </h1>

            <p
              className="muted-copy mt-4"
              style={{
                maxWidth: 620,
                fontSize: 14,
              }}
            >
              Account, session, and operational signal controls for the Glow
              booking desk.
            </p>
          </div>

          <div
            className="glass-card gradient-border settings-workspace-card"
            style={{
              padding: 18,
            }}
          >
            <div className="flex items-center gap-3">
              <span className="led led-green" />

              <div>
                <div className="mono-label">Live Workspace</div>

                <div
                  className="mt-1 font-serif"
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: 17,
                  }}
                >
                  Glow Hire
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* BODY */}
        <div
          className="grid grid-cols-[286px_1fr] gap-9 items-start settings-body"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* SIDEBAR */}
          <aside
            className="glass-card gradient-border settings-nav"
            style={{
              padding: 14,
              height: '100%',
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            <nav className="flex flex-col gap-1">
              {sections.map((item) => (
                <NavLink
                  key={item.id}
                  active={section === item.id}
                  label={item.label}
                  meta={item.meta}
                  onClick={() => setSection(item.id)}
                />
              ))}
            </nav>
          </aside>

          {/* CONTENT */}
          <section
            className="space-y-5 settings-content"
            style={{
              height: '100%',
              minHeight: 0,
              overflowY: 'auto',
              paddingRight: 6,
            }}
          >
            {section === 'profile' && <Profile />}
            {section === 'session' && (
              <SessionSection onSignOut={signOut} />
            )}
            {section === 'appearance' && <Appearance />}
            {section === 'notifications' && <Notifications />}
            {section === 'about' && <About />}
          </section>
        </div>
      </div>
    </main>
  );
}

function NavLink({
  active,
  label,
  meta,
  onClick,
}: {
  active: boolean;
  label: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left"
      style={{
        padding: '14px 14px',
        borderRadius: 8,
        border: '1px solid',
        borderColor: active
          ? brand.accentBorder
          : 'transparent',
        background: active
          ? brand.accentFaint
          : 'transparent',
        color: active
          ? 'var(--text-primary)'
          : brand.textSub,
        cursor: 'pointer',
        transition: 'background 150ms ease, border-color 150ms ease',
      }}
    >
      <span
        className="font-serif"
        style={{
          display: 'block',
          fontSize: 16,
        }}
      >
        {label}
      </span>

      <span
        className="mono-label mt-1 block"
        style={{
          color: active ? 'var(--accent)' : undefined,
        }}
      >
        {meta}
      </span>
    </button>
  );
}

function SectionHeader({
  kicker,
  title,
  copy,
}: {
  kicker: string;
  title: string;
  copy: string;
}) {
  return (
    <header className="mb-6">
      <span className="mono-label">{kicker}</span>

      <h2
        className="cinema-title mt-2"
        style={{
          fontSize: 31,
          lineHeight: 1.12,
        }}
      >
        {title}
      </h2>

      <p
        className="muted-copy mt-3"
        style={{
          fontSize: 13.5,
          maxWidth: 620,
        }}
      >
        {copy}
      </p>
    </header>
  );
}

function SettingsCard({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div
      className="glass-card gradient-border settings-card"
      style={{
        padding: 30,
      }}
    >
      {children}
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(
    user ? `${user.first_name} ${user.last_name}`.trim() : '',
  );
  const [cellNumber, setCellNumber] = useState(user?.cell_number ?? '');

  const initials =
    displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'GH';

  async function save() {
    setError(null);

    const [first_name, ...rest] = displayName.trim().split(' ');
    const last_name = rest.join(' ');

    try {
      const updated = await updateProfile({
        first_name: first_name ?? '',
        last_name,
        cell_number: cellNumber,
      });
      setUser(updated);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1400);
    } catch {
      setError('Could not save changes. Try again.');
    }
  }

  return (
    <>
      <SectionHeader
        kicker="Operator"
        title="Profile"
        copy="The visible identity attached to approvals, edits, and outbound quote decisions."
      />

      <SettingsCard>
        <div
          className="flex items-center gap-5 mb-8 pb-8 settings-avatar-row"
          style={{
            borderBottom: '1px solid var(--hairline)',
          }}
        >
          <div
            className="gradient-border font-serif"
            style={{
              width: 68,
              height: 68,
              borderRadius: 999,
              background: brand.hairlineSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 21,
            }}
          >
            {initials}
          </div>

          <div>
            <div
              className="font-serif"
              style={{
                fontSize: 23,
                color: 'var(--text-primary)',
              }}
            >
              {displayName || 'Unnamed operator'}
            </div>

            <div className="mono-label mt-2">
              {user?.email}
            </div>
          </div>

          <span
            className="noir-pill ml-auto settings-role-pill"
            style={{
              color: 'var(--accent)',
              borderColor: brand.accentBorder,
              background: 'var(--accent-tint)',
            }}
          >
            {user?.role ?? 'Booker'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-10 gap-y-7 settings-form-grid">
          <Field
            label="Display name"
            value={displayName}
            onChange={setDisplayName}
          />
          <Field
            label="Email"
            value={user?.email ?? ''}
            readOnly
          />
          <Field
            label="Cell number"
            value={cellNumber}
            onChange={setCellNumber}
          />
          <Field label="Time zone" value="Africa/Johannesburg" readOnly />
        </div>

        <FooterActions saved={saved} error={error} onSave={save} />
      </SettingsCard>

      <VerifyMobileSection />
      <ChangePasswordSection />
      <ChangeEmailSection currentEmail={user?.email ?? ''} />
    </>
  );
}

// Verifies the mobile number SAVED on the account (the server texts that number,
// never one typed here). Editing the number clears verification server-side, so
// the status below always reflects the saved value.
function VerifyMobileSection() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [verifying, setVerifying] = useState(false);
  const savedNumber = (user?.cell_number ?? '').trim();
  const verified = Boolean(user?.cell_number_verified);

  async function handleVerified() {
    setUser(await getMe());
    setVerifying(false);
    toast.success('Mobile number verified');
  }

  return (
    <SettingsCard>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif" style={{ color: 'var(--text-primary)', fontSize: 17 }}>
            Verify Mobile Number
          </div>
          <p className="muted-copy mt-1" style={{ fontSize: 12.5 }}>
            {savedNumber
              ? `A 4-digit code is texted to ${savedNumber}.`
              : 'Save a cell number above, then verify it here.'}
          </p>
        </div>

        <span
          className="noir-pill"
          style={{ color: verified ? 'var(--success)' : undefined }}
        >
          {verified ? 'Verified' : 'Not verified'}
        </span>
      </div>

      {savedNumber && !verified && !verifying && (
        <div className="mt-6 pt-6 flex items-center justify-between" style={{ borderTop: '1px solid var(--hairline)' }}>
          <span className="mono-label">Changed the number? Save it first.</span>
          <button className="noir-button noir-button-primary" onClick={() => setVerifying(true)}>
            Send Code
          </button>
        </div>
      )}

      {verifying && (
        <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--hairline)', maxWidth: 420, margin: '0 auto' }}>
          <VerificationStep initialChannel="sms" hasCellNumber allowSwitch={false} onVerified={handleVerified} />
          <div className="flex items-center justify-center mt-4">
            <button
              type="button"
              className="mono-label"
              onClick={() => setVerifying(false)}
              style={{ background: 'transparent', border: 'none', color: 'rgba(var(--fg-rgb),0.45)', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </SettingsCard>
  );
}

// Same policy as the forgot-password reset screen (ResetPasswordPage) —
// keep both in sync if the policy ever changes.
const PASSWORD_RULES: Array<{ id: string; label: string; test: (pw: string) => boolean }> = [
  { id: 'len', label: '12 characters minimum', test: (p) => p.length >= 12 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'num', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'sym', label: 'One symbol (!@#$%…)', test: (p) => /[^\w\s]/.test(p) },
];

function ChangePasswordSection() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rulePass = Object.fromEntries(
    PASSWORD_RULES.map((r) => [r.id, r.test(newPassword)]),
  ) as Record<string, boolean>;
  const allRulesMet = PASSWORD_RULES.every((r) => rulePass[r.id]);
  const confirmMatches = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit =
    currentPassword.trim().length > 0 && allRulesMet && confirmMatches && !submitting;

  function resetFields() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword, confirmPassword);
      toast.success('Password changed');
      resetFields();
      setOpen(false);
    } catch {
      // The backend distinguishes wrong-current-password (401) from a
      // new/confirm mismatch (400), but confirmMatches already blocks
      // submission on a client-side mismatch — so any error reaching here
      // in practice means the current password was wrong.
      setError('Current password is incorrect. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SettingsCard>
      <button
        onClick={() => {
          setOpen((v) => !v);
          setError(null);
        }}
        className="flex items-center justify-between w-full text-left"
        style={{ cursor: 'pointer' }}
      >
        <div>
          <div
            className="font-serif"
            style={{ color: 'var(--text-primary)', fontSize: 17 }}
          >
            Change Password
          </div>
          <p className="muted-copy mt-1" style={{ fontSize: 12.5 }}>
            Update the password used to sign in to this operator account.
          </p>
        </div>

        <span className="mono-label">{open ? 'Close' : 'Expand'}</span>
      </button>

      {open && (
        <div
          className="mt-7 pt-7"
          style={{ borderTop: '1px solid var(--hairline)' }}
        >
          <div className="grid grid-cols-2 gap-x-10 gap-y-7 settings-form-grid">
            <PasswordField
              label="Current password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              className="ghost-input mt-2"
            />
            <div />
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              className="ghost-input mt-2"
            />
            <PasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              className="ghost-input mt-2"
            />
          </div>

          <ul
            className="mt-7 space-y-2"
            style={{ listStyle: 'none', padding: 0 }}
          >
            {PASSWORD_RULES.map((r) => {
              const ok = rulePass[r.id];
              return (
                <li key={r.id} className="flex items-center gap-2.5">
                  <span className={`led ${ok ? 'led-green' : 'led-red'}`} aria-hidden />
                  <span
                    style={{
                      color: ok ? 'rgba(var(--fg-rgb),0.75)' : 'rgba(var(--fg-rgb),0.4)',
                      fontSize: 12.5,
                    }}
                  >
                    {r.label}
                  </span>
                </li>
              );
            })}
            <li className="flex items-center gap-2.5">
              <span className={`led ${confirmMatches ? 'led-green' : 'led-red'}`} aria-hidden />
              <span
                style={{
                  color: confirmMatches ? 'rgba(var(--fg-rgb),0.75)' : 'rgba(var(--fg-rgb),0.4)',
                  fontSize: 12.5,
                }}
              >
                Confirmation matches
              </span>
            </li>
          </ul>

          <div
            className="mt-8 pt-6 flex items-center justify-between"
            style={{ borderTop: '1px solid var(--hairline)' }}
          >
            <span
              className="mono-label"
              style={{ color: error ? brand.error : undefined }}
            >
              {error ?? 'Choose a new password meeting all requirements above'}
            </span>

            <button
              className="noir-button noir-button-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{ opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            >
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </div>
      )}
    </SettingsCard>
  );
}

function ChangeEmailSection({ currentEmail }: { currentEmail: string }) {
  const toast = useToast();
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [channel, setChannel] = useState<RecoveryChannel>('email');
  const hasMobile = Boolean((user?.cell_number ?? '').trim());
  // 'code': a code was sent (to the CURRENT email, or by SMS) and is awaiting entry.
  const [stage, setStage] = useState<'form' | 'code'>('form');
  const [sentTo, setSentTo] = useState('');
  const [sentChannel, setSentChannel] = useState<RecoveryChannel>('email');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  function sendVerification() {
    if (!newEmail.trim()) return;
    setConfirmError(null);
    setConfirmOpen(true);
  }

  async function handlePasswordConfirm(currentPassword: string) {
    setSubmitting(true);
    setConfirmError(null);
    try {
      const r = await requestEmailChangeCode(newEmail.trim(), currentPassword, channel);
      setSentTo(r.sent_to);
      setSentChannel(r.channel ?? channel);
      setStage('code');
      setConfirmOpen(false);
      toast.success(channel === 'sms' ? 'Confirmation code texted to you' : 'Confirmation code sent to your current email');
    } catch (err) {
      // Was a blanket "Incorrect password" on ANY failure — the backend
      // can also fail here for a reason that has nothing to do with the
      // password (e.g. the verification email itself couldn't be sent),
      // and telling the operator their password is wrong when it isn't
      // sends them retyping a correct password forever. Distinguish by
      // status: 401 really is the password check; anything else surfaces
      // the backend's real message instead of a guess.
      if (err instanceof ApiError && err.status === 401) {
        setConfirmError('Incorrect password. Please try again.');
      } else {
        setConfirmError(
          err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCode(code: string) {
    await confirmEmailChangeCode(code);
    // Refresh the signed-in user so the new address shows everywhere at once.
    setUser(await getMe());
    toast.success('Email address updated');
    setStage('form');
    setNewEmail('');
    setOpen(false);
  }

  return (
    <SettingsCard>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        style={{ cursor: 'pointer' }}
      >
        <div>
          <div
            className="font-serif"
            style={{ color: 'var(--text-primary)', fontSize: 17 }}
          >
            Change Email Address
          </div>
          <p className="muted-copy mt-1" style={{ fontSize: 12.5 }}>
            Update the email address linked to this operator account.
          </p>
        </div>

        <span className="mono-label">{open ? 'Close' : 'Expand'}</span>
      </button>

      {open && (
        <div
          className="mt-7 pt-7"
          style={{ borderTop: '1px solid var(--hairline)' }}
        >
          {stage === 'form' ? (
            <>
              <div className="grid grid-cols-2 gap-x-10 gap-y-7 settings-form-grid">
                <Field label="Current email" value={currentEmail} readOnly />
                <Field label="New email" value={newEmail} onChange={setNewEmail} />
              </div>

              <fieldset style={{ border: 'none', padding: 0, margin: '28px 0 0' }}>
                <legend className="mono-label" style={{ marginBottom: 8 }}>
                  Send my confirmation code by
                </legend>
                {([
                  ['email', 'Email (to your current address)'],
                  ['sms', hasMobile ? 'SMS (to your saved mobile number)' : 'SMS (save a mobile number above first)'],
                ] as Array<[RecoveryChannel, string]>).map(([ch, label]) => (
                  <label
                    key={ch}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 0',
                      cursor: ch === 'sms' && !hasMobile ? 'not-allowed' : 'pointer',
                      opacity: ch === 'sms' && !hasMobile ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="email-change-channel"
                      checked={channel === ch}
                      onChange={() => setChannel(ch)}
                      disabled={ch === 'sms' && !hasMobile}
                    />
                    <span style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'var(--color-on-surface)' }}>
                      {label}
                    </span>
                  </label>
                ))}
              </fieldset>

              <div
                className="mt-8 pt-6 flex items-center justify-between"
                style={{ borderTop: '1px solid var(--hairline)' }}
              >
                <span className="mono-label">
                  {channel === 'sms'
                    ? `A ${codeLength('sms')}-digit code will be texted to your saved mobile number`
                    : `A ${codeLength('email')}-digit code will be sent to your current email address`}
                </span>

                <button className="noir-button noir-button-primary" onClick={sendVerification}>
                  Send Code
                </button>
              </div>
            </>
          ) : (
            <div style={{ maxWidth: 420, margin: '0 auto' }}>
              <CodeEntry
                message={`We ${sentChannel === 'sms' ? 'texted' : 'sent'} a ${codeLength(sentChannel)}-digit code to ${sentTo}. Enter it to change your email to ${newEmail.trim()}.`}
                length={codeLength(sentChannel)}
                onVerify={handleCode}
                verifyLabel="Confirm Change"
              />
              <div className="flex items-center justify-center mt-4">
                <button
                  type="button"
                  className="mono-label"
                  onClick={() => setStage('form')}
                  style={{ background: 'transparent', border: 'none', color: 'rgba(var(--fg-rgb),0.45)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <PasswordConfirmModal
        open={confirmOpen}
        title="Confirm Email Change"
        description={`Enter your current password to send a confirmation code ${channel === 'sms' ? 'by SMS to your saved mobile number' : 'to your current email address'}. Your email will change to ${newEmail || 'the new address'} once you enter it.`}
        confirmLabel="Send Code"
        submitting={submitting}
        errorMessage={confirmError}
        onConfirm={handlePasswordConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </SettingsCard>
  );
}

function getDeviceLabel(): string {
  const ua = navigator.userAgent;

  let browser = 'Unknown browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = 'Unknown OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} · ${os}`;
}

function getRegionLabel(): string {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const city = timeZone.split('/').pop() ?? timeZone;
  return city.replace(/_/g, ' ');
}

function SessionSection({
  onSignOut,
}: {
  onSignOut: () => void;
}) {
  const { user, sessionStart } = useAuth();

  const signedInDate = sessionStart ?? (user?.date_joined ? new Date(user.date_joined) : null);
  const signedInLabel = signedInDate
    ? signedInDate.toLocaleString('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unavailable';

  return (
    <>
      <SectionHeader
        kicker="Active Connection"
        title="Session"
        copy="Encrypted cookie session with refresh rotation enabled for the operator console."
      />

      <SettingsCard>
        <div className="grid grid-cols-3 gap-5 settings-stat-grid">
          <Stat
            label="Signed in"
            value={signedInLabel}
          />

          <Stat
            label="Device"
            value={getDeviceLabel()}
          />

          <Stat
            label="Region"
            value={getRegionLabel()}
          />
        </div>

        <div
          className="mt-8 pt-7 flex items-center justify-between"
          style={{
            borderTop: '1px solid var(--hairline)',
          }}
        >
          <div className="flex items-center gap-3">
            <span className="led led-green" />

            <span
              className="muted-copy"
              style={{
                fontSize: 13,
              }}
            >
              Session is active.
            </span>
          </div>

          <button
            className="noir-button noir-button-danger"
            onClick={onSignOut}
          >
            Sign Out
          </button>
        </div>
      </SettingsCard>
    </>
  );
}

function Appearance() {
  const { theme, isOverridden, setTheme, resetToSystem } = useTheme();

  return (
    <>
      <SectionHeader
        kicker="Display"
        title="Appearance"
        copy="Choose light or dark. Defaults to your device's preference until you pick one manually."
      />

      <SettingsCard>
        <Toggle
          label="Light theme"
          hint={
            isOverridden
              ? 'Manually set — no longer follows your device preference.'
              : "Following your device's preference."
          }
          value={theme === 'light'}
          onChange={(v) => setTheme(v ? 'light' : 'dark')}
          last={!isOverridden}
        />

        {isOverridden && (
          <div
            className="mt-5 pt-5 flex items-center justify-between"
            style={{ borderTop: '1px solid var(--hairline)' }}
          >
            <span className="mono-label">Reset to match your device automatically</span>
            <button className="noir-button" onClick={resetToSystem}>
              Use System Setting
            </button>
          </div>
        )}
      </SettingsCard>
    </>
  );
}

function Notifications() {
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True only when the initial GET failed — the toggles below are then
  // showing hardcoded defaults, not the operator's real saved preferences.
  // Save is disabled in that state: PUT sends the whole prefs object, so
  // saving from an unknown starting point would silently overwrite every
  // real saved preference (not just the one the operator meant to change)
  // with defaults for the columns that never loaded correctly.
  const [loadFailed, setLoadFailed] = useState(false);
  const push = usePushSubscription();
  const [prefs, setPrefs] = useState<NotificationPreferencesUpdate>({
    new_booking_received: true,
    substitution_required: true,
    audit_log_alerts: false,
  });

  function loadPreferences() {
    setLoading(true);
    setLoadFailed(false);
    getNotificationPreferences()
      .then((data) => {
        setPrefs({
          new_booking_received: data.new_booking_received,
          substitution_required: data.substitution_required,
          audit_log_alerts: data.audit_log_alerts,
        });
      })
      .catch(() => {
        // Don't silently fall back to defaults — the operator needs to know
        // what's on screen may not match what's actually saved, and Save
        // must stay disabled until we know the real state.
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadPreferences();
  }, []);

  async function save() {
    if (loadFailed) return;
    setError(null);
    try {
      await updateNotificationPreferences(prefs);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1400);
    } catch {
      setError('Could not save preferences. Try again.');
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '24px 0' }}>
        <span className="mono-label">Loading preferences…</span>
      </div>
    );
  }

  return (
    <>
      <SectionHeader
        kicker="Alerts & Pings"
        title="Notifications"
        copy="Choose which alerts appear in GlowHire. Anything that arrives while you are signed out is waiting for you next time you log in."
      />

      <SettingsCard>
        {loadFailed && (
          <div
            className="mb-5 pb-5 flex items-center justify-between"
            style={{ borderBottom: '1px solid var(--hairline)' }}
          >
            <span className="mono-label" style={{ color: brand.error }}>
              Could not load your saved preferences — the toggles below are showing
              defaults, not your real settings. Saving is disabled until this loads.
            </span>
            <button className="noir-button" onClick={loadPreferences}>
              Retry
            </button>
          </div>
        )}

        <Toggle
          label="New booking received"
          hint="In-app alert when a new enquiry or client reply lands in the queue for review."
          value={prefs.new_booking_received}
          onChange={(v) => setPrefs((p) => ({ ...p, new_booking_received: v }))}
        />

        <Toggle
          label="Substitution required"
          hint="In-app alert when a booking needs you to accept or replace a substitute."
          value={prefs.substitution_required}
          onChange={(v) => setPrefs((p) => ({ ...p, substitution_required: v }))}
        />

        <Toggle
          label="Audit log alerts"
          hint="In-app alert for security and role-sensitive workflow events."
          value={prefs.audit_log_alerts}
          onChange={(v) => setPrefs((p) => ({ ...p, audit_log_alerts: v }))}
          last
        />

        <FooterActions saved={saved} error={error} onSave={save} saveDisabled={loadFailed} />
      </SettingsCard>

      <SectionHeader
        kicker="Live Updates"
        title="Push notifications"
        copy="Get an alert on this device even when GlowHire isn't open in a tab."
      />

      <SettingsCard>
        <Toggle
          label="Browser push"
          hint={
            push.status === 'unsupported'
              ? "This browser doesn't support push notifications."
              : push.status === 'denied'
                ? 'Blocked in your browser settings — enable notifications for this site to turn it back on.'
                : push.error
                  ? push.error
                  : 'New booking emails, replies, drafts ready for review, and failed or quarantined workflows.'
          }
          value={push.status === 'subscribed'}
          onChange={(v) => {
            if (push.status === 'unsupported' || push.status === 'denied' || push.isBusy) return;
            if (v) {
              void push.subscribe();
            } else {
              void push.unsubscribe();
            }
          }}
          last
        />
      </SettingsCard>
    </>
  );
}

function getEnvironmentLabel(): string {
  const env = import.meta.env.VITE_APP_ENV as string | undefined;
  if (env === 'staging') return 'Staging';
  if (env === 'production') return 'Production';
  return 'Development';
}

function About() {
  return (
    <>

      <SettingsCard>
        <div className="grid grid-cols-2 gap-6 settings-form-grid">
          <Stat
            label="Application"
            value="GlowHire Operator Console"
          />

          <Stat
            label="Version"
            value="1.0.0-stable"
          />

          <Stat
            label="Support"
            value="https://support@injozi.co.za"
          />

          <Stat
            label="Environment"
            value={getEnvironmentLabel()}
          />
        </div>
      </SettingsCard>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="block">
      <span className="mono-label">{label}</span>

      <input
        className="ghost-input mt-2"
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        style={readOnly ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
      />
    </label>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div
      className="noir-card"
      style={{
        padding: 18,
      }}
    >
      <div className="mono-label">{label}</div>

      <div
        className="mt-3 font-serif"
        style={{
          color: 'var(--text-primary)',
          fontSize: 17,
          lineHeight: 1.25,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  value,
  onChange,
  last = false,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between gap-8 py-5"
      style={{
        borderBottom: last
          ? 'none'
          : '1px solid var(--hairline)',
      }}
    >
      <div>
        <div
          className="font-serif"
          style={{
            color: 'var(--text-primary)',
            fontSize: 17,
          }}
        >
          {label}
        </div>

        <p
          className="muted-copy mt-1"
          style={{
            fontSize: 12.5,
          }}
        >
          {hint}
        </p>
      </div>

      <button
        onClick={() => onChange(!value)}
        aria-pressed={value}
        style={{
          width: 46,
          height: 24,
          borderRadius: 999,
          background: value
            ? brand.accentStrong
            : brand.hairline,
          border: `1px solid ${
            value
              ? brand.accentMid
              : 'var(--hairline)'
          }`,
          position: 'relative',
          cursor: 'pointer',
          transition:
            'background 150ms ease, border-color 150ms ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: value ? 25 : 3,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: '#fff',
            transition: 'left 150ms ease',
          }}
        />
      </button>
    </div>
  );
}

function FooterActions({
  saved,
  error,
  onSave,
  saveDisabled = false,
}: {
  saved: boolean;
  error?: string | null;
  onSave: () => void;
  saveDisabled?: boolean;
}) {
  return (
    <div
      className="mt-9 pt-6 flex items-center justify-between"
      style={{
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <span
        className="mono-label"
        style={{
          color: error
            ? brand.error
            : saved
              ? 'var(--success)'
              : undefined,
        }}
      >
        {error
          ? error
          : saved
            ? 'Saved'
            : saveDisabled
              ? 'Save disabled until preferences reload'
              : 'Unsaved changes stay local until saved'}
      </span>

      <button
        className="noir-button noir-button-primary"
        onClick={onSave}
        disabled={saveDisabled}
        style={saveDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        Save Changes
      </button>
    </div>
  );
}